import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, } from 'libphonenumber-js/max';
import { z } from 'zod';
/** Jordan is the default country of every phone input (customers.md section 9.1). */
export const DEFAULT_PHONE_COUNTRY = 'JO';
export const PHONE_ERROR_CODES = [
    'PHONE_REQUIRED',
    'PHONE_INVALID',
    'PHONE_NOT_MOBILE',
    'PHONE_COUNTRY_NOT_ALLOWED',
];
/**
 * Countries and territories whose phones are not accepted in any system:
 * Israel and all of Europe (customers.md section 9.7, owner decision of 24
 * October 2026). Hard-coded and identical in every system; it is not a
 * setting. Turkey, Russia, Cyprus, Georgia, Armenia, Azerbaijan, Kazakhstan,
 * the United States and the whole +1 plan stay accepted.
 */
// prettier-ignore
export const EXCLUDED_PHONE_COUNTRIES = [
    'IL', 'GB', 'IE', 'FR', 'DE', 'NL', 'BE', 'LU', 'CH', 'AT', 'LI', 'MC', 'AD',
    'ES', 'PT', 'IT', 'VA', 'SM', 'MT', 'GR', 'SE', 'NO', 'DK', 'FI', 'IS', 'PL',
    'CZ', 'SK', 'HU', 'RO', 'BG', 'MD', 'UA', 'BY', 'EE', 'LV', 'LT', 'SI', 'HR',
    'BA', 'RS', 'ME', 'MK', 'AL', 'XK', 'GI', 'FO', 'IM', 'JE', 'GG', 'AX', 'SJ',
];
const EXCLUDED = new Set(EXCLUDED_PHONE_COUNTRIES);
/** True for a country of `EXCLUDED_PHONE_COUNTRIES`. */
export function isExcludedPhoneCountry(country) {
    return country !== null && country !== undefined && EXCLUDED.has(country);
}
const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const EASTERN_ARABIC_INDIC_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
/** Spaces, dashes, dots, brackets and the invisible direction marks a paste may carry. */
const SEPARATORS = /[\s\-.()[\] ‎‏‪-‮⁦-⁩]/g;
/**
 * Converts Arabic-Indic and Eastern Arabic-Indic digits to Latin digits and
 * removes separators. A leading `+` is kept; `00` becomes `+`.
 */
export function cleanPhoneInput(input) {
    let result = '';
    for (const char of input) {
        const arabic = ARABIC_INDIC_DIGITS.indexOf(char);
        const eastern = EASTERN_ARABIC_INDIC_DIGITS.indexOf(char);
        if (arabic >= 0)
            result += String(arabic);
        else if (eastern >= 0)
            result += String(eastern);
        else if (char === '＋')
            result += '+';
        else
            result += char;
    }
    result = result.replace(SEPARATORS, '');
    if (result.startsWith('00'))
        result = `+${result.slice(2)}`;
    return result;
}
function isAcceptedType(phone) {
    const type = phone.getType();
    return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE';
}
/**
 * The region decides (Russia and Kazakhstan share +7; the United Kingdom, the
 * Isle of Man, Jersey and Guernsey share +44). Without a resolved region, the
 * number is excluded only when every region of its calling code is excluded.
 */
function isExcludedNumber(phone) {
    if (phone.country)
        return isExcludedPhoneCountry(phone.country);
    const regions = getCountries().filter((iso2) => getCountryCallingCode(iso2) === phone.countryCallingCode);
    return regions.length > 0 && regions.every((iso2) => isExcludedPhoneCountry(iso2));
}
function classify(phone, fallbackNational, options) {
    if (!phone)
        return { ok: false, error: 'PHONE_INVALID', nationalNumber: fallbackNational };
    const nationalNumber = String(phone.nationalNumber);
    if (!options.allowExcludedCountry && isExcludedNumber(phone)) {
        return {
            ok: false,
            error: 'PHONE_COUNTRY_NOT_ALLOWED',
            country: phone.country,
            nationalNumber,
        };
    }
    if (!phone.isValid()) {
        return { ok: false, error: 'PHONE_INVALID', country: phone.country, nationalNumber };
    }
    if (!isAcceptedType(phone)) {
        return { ok: false, error: 'PHONE_NOT_MOBILE', country: phone.country, nationalNumber };
    }
    return { ok: true, e164: phone.number, country: phone.country, nationalNumber };
}
/**
 * Parses what a user typed or pasted, or what another system sent.
 *
 * `country` is the country chosen in the picker (Jordan by default). The
 * input may be a national number with or without the trunk `0`, or a full
 * international number (`+962…`, `00962…`, or `962…` when it is not a valid
 * national number of the chosen country). Arabic-Indic digits, spaces,
 * dashes, dots and brackets are tolerated.
 *
 * A number of an excluded country is refused with `PHONE_COUNTRY_NOT_ALLOWED`
 * unless `options.allowExcludedCountry` is set (see `PhoneParseOptions`).
 */
export function parsePhone(input, country = DEFAULT_PHONE_COUNTRY, options = {}) {
    const cleaned = cleanPhoneInput(input ?? '');
    if (cleaned === '' || cleaned === '+')
        return { ok: false, error: 'PHONE_REQUIRED' };
    if (!/^\+?\d+$/.test(cleaned))
        return { ok: false, error: 'PHONE_INVALID' };
    if (cleaned.startsWith('+')) {
        return classify(parsePhoneNumberFromString(cleaned), cleaned.slice(1), options);
    }
    const national = parsePhoneNumberFromString(cleaned, country);
    if (national?.isValid())
        return classify(national, cleaned, options);
    // `962791234567` with Jordan chosen: not a national number, so it is read
    // as an international one without its `+`.
    const international = parsePhoneNumberFromString(`+${cleaned}`);
    if (international?.isValid())
        return classify(international, cleaned, options);
    return classify(national, cleaned.replace(/^0+/, ''), options);
}
/**
 * The E.164 value of an accepted mobile, or `null`. A look-up of a stored
 * phone passes `{ allowExcludedCountry: true }` so stored excluded-country
 * phones are still found (customers.md section 9.7).
 */
export function normalizePhone(input, country = DEFAULT_PHONE_COUNTRY, options = {}) {
    const result = parsePhone(input, country, options);
    return result.ok ? result.e164 : null;
}
/**
 * True when the number belongs to an excluded country (customers.md section
 * 9.7). A service that read a phone with `storedPhoneSchema` refuses it with
 * `PHONE_COUNTRY_NOT_ALLOWED` when this is true and the phone is new or
 * changed.
 */
export function isExcludedCountryPhone(input) {
    const result = parsePhone(input);
    return !result.ok && result.error === 'PHONE_COUNTRY_NOT_ALLOWED';
}
/**
 * True when `next` is the same phone as the stored `stored` value after
 * normalisation, including a stored phone of an excluded country. A record
 * whose phone is unchanged saves even when that phone is of an excluded
 * country (customers.md section 9.7).
 */
export function isSameStoredPhone(next, stored) {
    const options = { allowExcludedCountry: true };
    const a = normalizePhone(next, DEFAULT_PHONE_COUNTRY, options);
    return a !== null && a === normalizePhone(stored, DEFAULT_PHONE_COUNTRY, options);
}
/** `+962791234567` -> `+962 7 9123 4567` (the library's international format), for read-only places; the input when it cannot be parsed. */
export function formatPhoneInternational(phone) {
    if (!phone)
        return '';
    const parsed = parsePhoneNumberFromString(cleanPhoneInput(phone));
    return parsed?.isValid() ? parsed.formatInternational() : phone;
}
/**
 * Every country libphonenumber-js knows, with its dial code, excluded ones
 * included (for reading a stored value). The picker uses
 * `listAllowedPhoneCountries`.
 */
export function listPhoneCountries() {
    return getCountries().map((iso2) => ({ iso2, dialCode: `+${getCountryCallingCode(iso2)}` }));
}
/** The countries the picker offers: every country except the excluded ones (customers.md sections 9.1 and 9.7). */
export function listAllowedPhoneCountries() {
    return listPhoneCountries().filter((entry) => !isExcludedPhoneCountry(entry.iso2));
}
export function isPhoneCountry(value) {
    return getCountries().includes(value);
}
/**
 * Zod schema for a required mobile phone: accepts any form `parsePhone`
 * accepts (with Jordan as the country of a national number) and outputs
 * E.164. A refusal is a custom issue whose message and `params.code` are the
 * stable error code (`PHONE_REQUIRED`, `PHONE_INVALID`, `PHONE_NOT_MOBILE`,
 * `PHONE_COUNTRY_NOT_ALLOWED`). A form that edits a stored phone uses
 * `storedPhoneSchema` and checks the change with `isSameStoredPhone`.
 */
export const phoneSchema = z.unknown().transform((value, ctx) => {
    if (value !== undefined && value !== null && typeof value !== 'string') {
        ctx.addIssue({ code: 'custom', message: 'PHONE_INVALID', params: { code: 'PHONE_INVALID' } });
        return z.NEVER;
    }
    const result = parsePhone(value);
    if (!result.ok) {
        ctx.addIssue({ code: 'custom', message: result.error, params: { code: result.error } });
        return z.NEVER;
    }
    return result.e164;
});
/**
 * Zod schema for a required phone of a record being edited: like
 * `phoneSchema`, but a phone of an excluded country passes, so the service
 * can compare it with the stored one (`isSameStoredPhone`) and refuse it with
 * `PHONE_COUNTRY_NOT_ALLOWED` only when it is new or changed (customers.md
 * section 9.7).
 */
export const storedPhoneSchema = z.unknown().transform((value, ctx) => {
    if (value !== undefined && value !== null && typeof value !== 'string') {
        ctx.addIssue({ code: 'custom', message: 'PHONE_INVALID', params: { code: 'PHONE_INVALID' } });
        return z.NEVER;
    }
    const result = parsePhone(value, DEFAULT_PHONE_COUNTRY, { allowExcludedCountry: true });
    if (!result.ok) {
        ctx.addIssue({ code: 'custom', message: result.error, params: { code: result.error } });
        return z.NEVER;
    }
    return result.e164;
});
//# sourceMappingURL=phone.js.map